import { useMemo, useState } from 'react';
import type { Bookmaker } from '../types';

interface Props {
  bookmakers: Bookmaker[];
  selected: number[];
  onToggle: (id: number) => void;
}

const TOP_N_DEFAULT = 8;

// Hand-curated priority for the most-recognised UK brands. Lower = higher.
// Anything not in this list falls back to alphabetical after the top 10.
const PRIORITY: Record<string, number> = {
  bet365: 1,
  'paddy power': 2,
  'william hill': 3,
  betfair: 4,
  betfred: 5,
  'sky bet': 6,
  ladbrokes: 7,
  coral: 8,
  'betmgm uk': 9,
  betmgm: 9,
  betway: 10,
  unibet: 11,
  'mr green': 12,
  'virgin bet': 13,
  netbet: 14,
  '888sport': 15,
  '888': 15,
};

function priority(name: string): number {
  const key = name.toLowerCase().trim();
  return PRIORITY[key] ?? 999;
}

function rankBookmakers(list: Bookmaker[]): Bookmaker[] {
  return [...list].sort((a, b) => {
    const pa = priority(a.name);
    const pb = priority(b.name);
    if (pa !== pb) return pa - pb;
    return a.name.localeCompare(b.name);
  });
}

export function BookmakerPicker({ bookmakers, selected, onToggle }: Props) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const ranked = useMemo(() => rankBookmakers(bookmakers), [bookmakers]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ranked;
    return ranked.filter((b) => b.name.toLowerCase().includes(q));
  }, [ranked, query]);

  const visible =
    query.trim() || showAll ? filtered : filtered.slice(0, TOP_N_DEFAULT);

  const selectedBookmakers = useMemo(
    () => bookmakers.filter((b) => selectedSet.has(b.id)),
    [bookmakers, selectedSet]
  );

  return (
    <div className="fb-bookmakers">
      {selectedBookmakers.length > 0 && (
        <div className="fb-bookmakers__chips" aria-label="Selected bookmakers">
          {selectedBookmakers.map((b) => (
            <button
              key={b.id}
              type="button"
              className="fb-bookmakers__chip"
              onClick={() => onToggle(b.id)}
              aria-label={`Remove ${b.name}`}
            >
              {b.name}
              <span className="fb-bookmakers__chip-remove" aria-hidden="true">
                ×
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="fb-bookmakers__search-wrap">
        <span className="fb-bookmakers__search-icon" aria-hidden="true">
          🔍
        </span>
        <input
          type="search"
          className="fb-bookmakers__search"
          placeholder="Type to find your bookmaker…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search bookmakers"
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {visible.length === 0 ? (
        <p className="fb-bookmakers__empty">
          No bookmaker matches &ldquo;{query}&rdquo;. Skip if yours isn&rsquo;t listed.
        </p>
      ) : (
        <div className="fb-bookmakers__list" role="listbox" aria-multiselectable="true">
          {visible.map((b) => {
            const isSelected = selectedSet.has(b.id);
            return (
              <button
                key={b.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={
                  'fb-bookmakers__item' +
                  (isSelected ? ' fb-bookmakers__item--selected' : '')
                }
                onClick={() => onToggle(b.id)}
              >
                <span
                  className="fb-bookmakers__logo"
                  style={{ background: b.logoBgColor }}
                  aria-hidden="true"
                >
                  {b.logoUrl ? (
                    <img
                      src={b.logoUrl}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    b.logoInitials
                  )}
                </span>
                <span className="fb-bookmakers__name">{b.name}</span>
                {isSelected && (
                  <span className="fb-bookmakers__check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {!query.trim() && !showAll && filtered.length > TOP_N_DEFAULT && (
        <button
          type="button"
          className="fb-bookmakers__more"
          onClick={() => setShowAll(true)}
        >
          Show all {filtered.length} bookmakers
        </button>
      )}
      {!query.trim() && (
        <p className="fb-bookmakers__count">
          Showing {visible.length} of {bookmakers.length} · skip if yours isn&rsquo;t listed
        </p>
      )}
    </div>
  );
}
